import { Typography } from "@mui/material";
import { useCallback, useMemo } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../api/adminApi";
import type { LookupObjectReference } from "../../../api/types/objectTypes";
import MultiselectAutocomplete from "../../common/MultiselectAutocomplete";
import { getLookupOptionDisplayValue, isStringArray } from "../helpers";

interface Props {
  objectType: string;
  value: unknown;
  label: string;
  onChange?: (ids: string[]) => void;
  disabled?: boolean;
  error?: boolean;
  readOnly?: boolean;
}

const getOptionLabel = (option: LookupObjectReference): string =>
  getLookupOptionDisplayValue(option.objectId, [option]) ?? option.objectId;
const getOptionValue = (option: LookupObjectReference): string => option.objectId;

const MultiLookupField = ({ objectType, value, label, onChange, disabled, error, readOnly }: Props) => {
  const loadOptions = useCallback(() => adminApi.getObjectsAsLookupReferences(objectType), [objectType]);
  const [options, fetchError] = useFetch(loadOptions);
  const values = useMemo(() => {
    const ids = isStringArray(value) ? value : [];
    return ids.map(
      (id) => options?.find((option) => option.objectId === id) ?? { objectId: id, objectType, primaryFieldValue: id }
    );
  }, [options, value, objectType]);

  if (readOnly && values.length === 0) {
    return null;
  }

  if (fetchError) {
    logError(fetchError, "[MultiLookupField] getObjectsAsLookupReferences");
    return <DataLoadingFailed title={`Failed to load ${label.toLowerCase()}`} />;
  }

  if (options === undefined) {
    return <InlineLoader />;
  }

  if (readOnly) {
    return <Typography>{values.map(getOptionLabel).join(", ")}</Typography>;
  }

  return (
    <MultiselectAutocomplete
      dense
      options={options}
      values={values}
      label={label}
      placeholder={`Select ${label.toLowerCase()}`}
      disabled={disabled}
      error={error}
      onSelected={(items) => onChange?.(items.map(getOptionValue))}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
    />
  );
};

export default MultiLookupField;
